import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Film, CheckCircle2, ShieldQuestion, Loader2, Package, Scissors, Info, VideoOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { saas, errMsg } from "@/lib/saasApi";
import { useI18n, usePageTitle } from "@/i18n";

function VideoCard({ v, index, ratio, projectId }) {
  const { t } = useI18n();
  const [previewError, setPreviewError] = useState(false);
  const [clipsBusy, setClipsBusy] = useState(false);
  const clips = v.clips || [];
  const clipsReady = clips.length > 0;
  const aspect = ratio === "portrait" ? "aspect-[9/16]" : "aspect-video";

  const downloadClips = async () => {
    if (clipsBusy || !clipsReady) return;
    setClipsBusy(true);
    try {
      const res = await saas.videoClipsZip(projectId, index);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `option-${index + 1}-clips.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      toast.error(t("app.project.clipsError"));
    } finally {
      setClipsBusy(false);
    }
  };

  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-re-line bg-white shadow-sm transition-all duration-200 hover:border-re-red/30 hover:shadow-lg" data-testid={`video-card-${index}`}>
      <div className={`relative ${aspect} bg-black`}>
        {previewError ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
            <VideoOff className="h-6 w-6 text-white/70" />
            <p className="text-xs text-white/80" data-testid={`preview-error-${index}`}>{t("app.project.previewError")}</p>
          </div>
        ) : (
          <video src={v.url} controls playsInline preload="metadata" onError={() => setPreviewError(true)} className="h-full w-full object-contain" data-testid={`video-player-${index}`} />
        )}
        <span className="pointer-events-none absolute left-3 top-3 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm" data-testid={`lowres-badge-${index}`}>{t("app.project.lowRes")}</span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center justify-between">
          <p className="font-heading text-base font-semibold text-re-charcoal" data-testid={`option-label-${index}`}>{t("app.project.option", { n: index + 1 })}</p>
          <span className="text-sm text-re-muted" data-testid={`clip-count-${index}`}>{clipsReady ? t("app.project.clipsCount", { n: clips.length }) : t("app.project.clipsProcessingShort")}</span>
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <a href={v.url} download target="_blank" rel="noreferrer" data-testid={`video-download-${index}`}>
            <Button className="h-11 w-full rounded-xl bg-re-red text-white shadow-sm transition-colors hover:bg-re-deepred focus-visible:ring-2 focus-visible:ring-re-red/40"><Download className="mr-2 h-4 w-4" /> {t("app.project.downloadVideo")}</Button>
          </a>
          <Button variant="outline" onClick={downloadClips} disabled={!clipsReady || clipsBusy} data-testid={`clips-download-${index}`} className="h-11 w-full rounded-xl border-re-line text-re-charcoal transition-colors hover:border-re-red/40 hover:bg-re-soft disabled:opacity-100 disabled:text-re-muted">
            {clipsBusy ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("app.project.preparingClips")}</>
            ) : !clipsReady ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin opacity-60" /> {t("app.project.clipsStillProcessing")}</>
            ) : (
              <><Scissors className="mr-2 h-4 w-4" /> {t("app.project.downloadClips")}</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  usePageTitle("titles.project");
  const [project, setProject] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [zip, setZip] = useState({ status: "idle", url: null });
  const [clipsBusy, setClipsBusy] = useState(false);
  const pollRef = useRef(null);
  const zipRef = useRef(null);
  const loadingVideos = useRef(false);

  const fetchVideos = useCallback(async () => {
    if (loadingVideos.current) return;
    loadingVideos.current = true;
    try {
      const { data } = await saas.loadVideos(id);
      if (data.videos) setVideos(data.videos);
    } catch { /* keep showing processing */ }
    finally { loadingVideos.current = false; }
  }, [id]);

  const load = useCallback(async () => {
    try {
      const { data } = await saas.getProject(id);
      setProject(data);
      if (data.state === "COMPLETED") {
        if (data.videos && data.videos.length) setVideos(data.videos);
        else await fetchVideos();
      }
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [id, fetchVideos]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!project || project.state !== "PROCESSING") return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await saas.status(id);
        setProject((prev) => ({ ...prev, ...data }));
        if (data.state === "COMPLETED") { clearInterval(pollRef.current); await fetchVideos(); }
        else if (data.state === "SUPPORT_REVIEW") { clearInterval(pollRef.current); }
      } catch { /* transient */ }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [project, id, fetchVideos]);

  const startZip = async () => {
    setZip({ status: "working", url: null });
    try {
      const { data } = await saas.export(id);
      if (data.downloadUrl) return setZip({ status: "ready", url: data.downloadUrl });
      const jobId = data.jobId;
      zipRef.current = setInterval(async () => {
        try {
          const r = await saas.exportStatus(id, jobId);
          if (r.data.downloadUrl && r.data.status === "ready") { clearInterval(zipRef.current); setZip({ status: "ready", url: r.data.downloadUrl }); }
          else if (r.data.status === "error") { clearInterval(zipRef.current); setZip({ status: "idle", url: null }); toast.error(t("app.project.videoError")); }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (e) {
      setZip({ status: "idle", url: null });
      toast.error(errMsg(e));
    }
  };
  useEffect(() => () => { clearInterval(zipRef.current); }, []);

  const hasClips = videos.some((v) => (v.clips || []).length > 0);
  const allClipsReady = videos.length > 0 && videos.every((v) => (v.clips || []).length > 0);

  // While a completed project still has an option whose clips aren't ready yet, quietly re-fetch
  // (provider GET only — no credits, no render) so the per-option "Download clips" buttons enable
  // automatically. Bounded to a few attempts so we never poll indefinitely.
  useEffect(() => {
    if (!project || project.state !== "COMPLETED" || allClipsReady) return undefined;
    let n = 0;
    const iv = setInterval(() => {
      n += 1;
      fetchVideos();
      if (n >= 6) clearInterval(iv);
    }, 20000);
    return () => clearInterval(iv);
  }, [project, allClipsReady, fetchVideos]);

  const downloadAllClips = async () => {
    if (clipsBusy || !hasClips) return;
    setClipsBusy(true);
    try {
      const res = await saas.clipsZip(id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const slug = (project?.projectName || "project").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase() || "project";
      a.download = `${slug}-clips.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      let msg = t("app.project.videoError");
      if (e?.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* */ } }
      toast.error(msg);
    } finally {
      setClipsBusy(false);
    }
  };

  if (loading) return <p className="text-sm text-re-muted" data-testid="pd-loading">{t("app.project.loading")}</p>;
  if (error) return <p className="text-sm text-re-red" data-testid="pd-error">{error}</p>;
  if (!project) return null;

  const state = project.state;
  const pct = typeof project.progress === "number" ? Math.max(5, Math.min(99, project.progress)) : null;

  return (
    <div className="space-y-6" data-testid="project-detail">
      <button onClick={() => nav("/dashboard")} data-testid="pd-back" className="inline-flex items-center gap-1.5 text-sm text-re-muted hover:text-re-charcoal">
        <ArrowLeft className="h-4 w-4" /> {t("app.project.back")}
      </button>

      <div className="rounded-3xl border border-re-line bg-white p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-re-charcoal" data-testid="pd-name">{project.projectName}</h1>
            <p className="mt-1 text-sm text-re-muted">{project.ratio === "portrait" ? t("app.dashboard.portrait") : t("app.dashboard.landscape")} · {project.photoCount} {t("app.dashboard.photos")}</p>
          </div>
        </div>

        {state === "PROCESSING" && (
          <div className="mt-6 rounded-2xl bg-re-soft p-5 md:p-6" data-testid="pd-processing">
            <div className="flex items-center gap-2 text-re-charcoal">
              <Loader2 className="h-5 w-5 animate-spin text-re-red" />
              <p className="font-heading text-base font-semibold" data-testid="pd-processing-title">{t("app.project.processingTitle")}</p>
            </div>
            <p className="mt-2 text-sm text-re-muted">{t("app.project.processing1")}</p>
            <p className="mt-1 text-sm text-re-muted">{t("app.project.processing2")}</p>
            <div className="mt-4"><Progress value={pct ?? 15} className="h-2" data-testid="pd-progress" />{pct !== null && <p className="mt-1.5 text-xs text-re-muted">{pct}%</p>}</div>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Button onClick={() => nav("/dashboard")} data-testid="pd-back-to-dashboard" variant="outline" className="rounded-full"><ArrowLeft className="mr-1.5 h-4 w-4" /> {t("app.project.backToDashboard")}</Button>
              <span className="text-xs text-re-muted">{t("app.project.keepHint")}</span>
            </div>
          </div>
        )}

        {state === "SUPPORT_REVIEW" && (
          <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50 p-5" data-testid="pd-review">
            <div className="flex items-center gap-2 text-re-charcoal"><ShieldQuestion className="h-5 w-5 text-blue-600" /><p className="font-medium">{t("app.project.reviewTitle")}</p></div>
            <p className="mt-1 text-sm text-re-muted">{t("app.project.reviewText")}</p>
          </div>
        )}

        {state === "CONCEPT" && (
          <div className="mt-6 rounded-2xl bg-re-soft p-5 text-sm text-re-muted" data-testid="pd-concept">{t("app.project.conceptText")}</div>
        )}

        {state === "COMPLETED" && (
          <div className="mt-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="h-5 w-5" /><p className="text-sm font-medium" data-testid="pd-completed">{t("app.project.readyBadge")}</p>
                </div>
                <h2 className="mt-2 font-heading text-2xl font-bold text-re-charcoal" data-testid="pd-results-count">{t("app.project.resultsCount", { n: videos.length })}</h2>
                <p className="mt-1 max-w-md text-sm text-re-muted">{t("app.project.resultsSub")}</p>
              </div>
              {videos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {hasClips && (
                    <Button onClick={downloadAllClips} disabled={clipsBusy} variant="outline" data-testid="clips-zip-start" className="rounded-full">
                      {clipsBusy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("app.project.preparingClips")}</> : <><Scissors className="mr-1.5 h-4 w-4" /> {t("app.project.downloadAllClips")}</>}
                    </Button>
                  )}
                  {zip.status === "ready" ? (
                    <a href={zip.url} download target="_blank" rel="noreferrer" data-testid="zip-download"><Button className="rounded-full bg-re-red text-white hover:bg-re-deepred"><Download className="mr-1.5 h-4 w-4" /> {t("app.project.downloadZip")}</Button></a>
                  ) : (
                    <Button onClick={startZip} disabled={zip.status === "working"} data-testid="zip-start" className="rounded-full bg-re-red text-white hover:bg-re-deepred">
                      {zip.status === "working" ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("app.project.preparing")}</> : <><Package className="mr-1.5 h-4 w-4" /> {t("app.project.downloadAllVideos")}</>}
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4 flex items-start gap-2 rounded-xl border border-re-line bg-re-soft px-4 py-2.5 text-xs text-re-muted" data-testid="quality-note">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-re-red" />
              <span>{t("app.project.qualityNote")}</span>
            </div>

            {!allClipsReady && videos.length > 0 && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-re-muted" data-testid="clips-preparing">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("app.project.clipsPreparing")}
              </p>
            )}

            {videos.length === 0 ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-re-muted"><Loader2 className="h-4 w-4 animate-spin" /> {t("app.project.loadingVideos")}</div>
            ) : (
              <div className="mt-5 grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="videos-grid">
                {videos.map((v, i) => <VideoCard key={v.id || i} v={v} index={i} ratio={project.ratio} projectId={id} />)}
              </div>
            )}
          </div>
        )}

        {state !== "COMPLETED" && state !== "PROCESSING" && state !== "SUPPORT_REVIEW" && state !== "CONCEPT" && (
          <div className="mt-6 flex items-center gap-2 text-sm text-re-muted"><Film className="h-4 w-4" /> —</div>
        )}
      </div>
    </div>
  );
}
